/**
 * Atribución de campaña — captura en el arranque de la app.
 *
 * POR QUÉ ESTO CORRE EN `index.js` Y NO EN `Contratar.js`
 * ───────────────────────────────────────────────────────
 * La landing manda a `app.pux.app/contratar?utm_source=…`. Un visitante SIN
 * sesión nunca llega a montar `<Contratar/>`: `App.js` lo manda a `/` con
 * `<Navigate to="/" replace />`, que NO conserva el querystring. Para cuando
 * el usuario se autentica y el gate de onboarding lo devuelve a `/contratar`,
 * los parámetros ya no existen en la URL.
 *
 * O sea: leerlos desde el componente perdería la atribución de TODO visitante
 * que llega desde la landing sin sesión, que es la mayoría. Por eso la captura
 * ocurre una sola vez, en el arranque, antes de que el router decida nada.
 *
 * `sessionStorage` y no `localStorage`: la atribución pertenece a esta visita.
 * En `localStorage` una campaña de hace tres semanas se llevaría el crédito de
 * una compra que originó otra cosa.
 *
 * `fbclid` — el identificador de clic de Meta — se captura APARTE (`getFbclid`)
 * y no entra en `getAttribution()`: no es atribución propia y no va a Stripe en
 * crudo. `utils/metaPixel.js` lo usa para reconstruir `_fbc` cuando la cookie
 * del pixel no existe (bloqueador en la landing, cookie caducada), con la marca
 * de tiempo de captura que el formato de Meta exige.
 */

const KEY = 'pux_attr';
const KEY_FBCLID = 'pux_fbclid';

const UTM_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

// Campos propios que emite la landing (ver landing/landing.js, attributionParams).
const PUX_FIELDS = ['pux_referrer', 'pux_t0', 'pux_origin'];

const ALL_FIELDS = [...UTM_FIELDS, ...PUX_FIELDS];

// Tope por valor. `metadata` de Stripe admite 500 chars por valor; se recorta
// acá para que el límite viva junto al dato y no en el punto de envío, donde
// es fácil olvidarlo al agregar un campo nuevo.
const MAX_VALUE_LEN = 480;

/**
 * Safari en modo privado LANZA al tocar sessionStorage. Sin envoltorio, un
 * throw acá reventaría el arranque de la app entera.
 */
function safe(fn, fallback) {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

/**
 * Primer toque gana: si ya hay atribución guardada en esta pestaña, no se pisa.
 * Un usuario que navega /contratar → /dashboard → /contratar no debe perder el
 * origen por el camino.
 */
export function captureAttributionFromUrl() {
  const qs = safe(() => new URLSearchParams(window.location.search), null);
  if (!qs) return;

  safe(() => {
    if (sessionStorage.getItem(KEY)) return;

    const data = {};
    ALL_FIELDS.forEach((f) => {
      const v = qs.get(f);
      if (v) data[f] = v.slice(0, MAX_VALUE_LEN);
    });

    // Sin un solo campo no se escribe nada: un objeto vacío bloquearía la
    // captura de una visita posterior que sí traiga parámetros.
    if (Object.keys(data).length === 0) return;

    sessionStorage.setItem(KEY, JSON.stringify(data));
  });

  // `fbclid` con su propia clave y su propio «primer toque»: puede llegar en
  // una visita que no trae UTMs (un anuncio de Meta sin parámetros propios) y
  // viceversa. Sólo se acepta la forma que Meta emite; cualquier otra cosa no
  // es un fbclid y no sirve para construir `_fbc`.
  safe(() => {
    if (sessionStorage.getItem(KEY_FBCLID)) return;
    const fbclid = qs.get('fbclid');
    if (!fbclid || !/^[A-Za-z0-9_-]{1,512}$/.test(fbclid)) return;
    sessionStorage.setItem(KEY_FBCLID, JSON.stringify({ fbclid, ts: Date.now() }));
  });
}

/** @returns {Object} atribución de esta visita, o `{}` si no hay. */
export function getAttribution() {
  return safe(() => JSON.parse(sessionStorage.getItem(KEY) || '{}'), {}) || {};
}

/** @returns {{fbclid: string, ts: number}|null} el clic de Meta de esta visita, si lo hubo. */
export function getFbclid() {
  const v = safe(() => JSON.parse(sessionStorage.getItem(KEY_FBCLID) || 'null'), null);
  return v && typeof v.fbclid === 'string' && typeof v.ts === 'number' ? v : null;
}
